/**
 * Users → Activity: the data model.
 *
 * Everything the screen knows about the shape of
 * `GET /auth/admin/user-activity` lives here — the metric vocabulary, the two
 * folds (by person / by calendar day), the automation test and the date
 * helpers. The screen and its cell renderers import from this; nothing here
 * touches React.
 *
 * Contract: ../eastview-os/frontend_documentation/user-activity-view.md
 */
import { amber, slate, status, steel, teal } from "../../../theme/colors";
import { formatDate } from "../../../_src_shared/utils/functions/formatDate";
import {
  convertToEasternTime,
  normalizeIsoStringToUtc,
} from "../../../_src_shared/utils/functions/formatDateTime";

/**
 * The condition topics, kept only as a FALLBACK.
 *
 * The backend now has a real `conditions` category (2026-09-01), so
 * `categories.conditions` is the number. Before that it folded condition work
 * into `data_updates` and the FE recovered it from the raw `event_types` tally
 * — that path survives here for a response served by an older deploy, and
 * `metricsOf` picks whichever is actually present.
 */
export const CONDITION_TOPICS = [
  "underwriting_conditions",
  "acquisition_underwriting_conditions",
  "acquisition_conditions_and_stips",
];

export const num = (v) => Number(v) || 0;
const cat = (r, k) => num(r?.categories?.[k]);
const hasCat = (r, k) => r?.categories && k in r.categories;

/**
 * Automation vs a person — **the backend's answer**, `isAutomation`.
 *
 * It knows things the client can't: the service-account list, and the raw
 * Salesforce user ids (`005…`) that `salesforce_field_history` events are
 * attributed to, which have no LOS account and rendered as unreadable strings
 * in the user list.
 *
 * The heuristic below is the fallback for a response served before the flag
 * existed (2026-09-01). It guessed from the address — literal `System`, an
 * actor with no `@`, or a local part that names a robot — and it could only
 * ever have been a guess. Prefer the flag; never "improve" the guess.
 */
const SYSTEM_ACCOUNTS = new Set(["automation@eastviewinv.com"]);
const SYSTEM_LOCAL_PARTS = new Set([
  "automation",
  "noreply",
  "no-reply",
  "system",
  "service",
]);

const looksLikeAutomation = (r) => {
  const u = (r?.user || "").toLowerCase().trim();
  if (!u) return false;
  if (u === "system" || SYSTEM_ACCOUNTS.has(u)) return true;
  if (!u.includes("@")) return true;
  const local = u.split("@")[0];
  return (
    SYSTEM_LOCAL_PARTS.has(local) ||
    local.endsWith("_sync") ||
    local.endsWith("-sync")
  );
};

export const isSystem = (r) =>
  typeof r?.isAutomation === "boolean" ? r.isAutomation : looksLikeAutomation(r);

/**
 * The five categories that ARE a person editing the loan file. This set is the
 * definition of "edits" on this screen.
 *
 * Deliberately NOT `write_events`. That is the backend's coarse read/write
 * split, and it still counts task work, Salesforce chatter, agent writes and
 * anything unclassified as a "write" — which is the exact shape of the bug the
 * 2026-09-01 category rewrite fixed on the backend. Leading with it here would
 * reintroduce the distortion one level up.
 *
 * Colors are the product's, not a chart library's: brand teal for the bulk of
 * the work, the second accent (steel) and the two status hues for the smaller
 * slices. Steel sits BETWEEN the two teals on purpose — they are the closest
 * pair in the set, so nothing puts them side by side.
 */
export const SERIES = [
  { key: "dataEdits", label: "Data", color: teal[500] },
  { key: "conditions", label: "Conditions", color: steel.main },
  { key: "comments", label: "Comments", color: teal[700] },
  { key: "exceptions", label: "Exceptions", color: amber.main },
  { key: "creations", label: "Creations", color: status.success.solid },
];

/**
 * Activity that is real, counted, and NOT editing: task-list work, work done in
 * Salesforce, and agent/AI/sync writes. Shown in its own block so it is visible
 * without ever being added to the edits number.
 *
 * `unclassified` is a **canary**, not a category: the backend maps every topic
 * it knows and drops anything else here rather than into `data_updates`. Any
 * non-zero value means a new audit topic shipped without a mapping — the screen
 * says so out loud rather than quietly absorbing it.
 */
export const OTHER_SERIES = [
  { key: "tasks", label: "Tasks", color: slate[500] },
  { key: "salesforce", label: "Salesforce", color: slate[400] },
  { key: "automation", label: "Automation", color: slate[600] },
  { key: "unclassified", label: "Unclassified", color: status.error.solid },
];

/**
 * Reads. Downloads are split out from views because taking a COPY out of the
 * system is a different (compliance-relevant) act from opening a file to read
 * it — and because a bulk zip download emits one event PER FILE, which is what
 * made downloads read as thousands of "edits" before the rewrite.
 */
export const VIEW_SERIES = [
  { key: "loanViews", label: "Loan opens", color: slate[300] },
  { key: "docViews", label: "Doc views", color: slate[300] },
  { key: "docDownloads", label: "Downloads", color: slate[400] },
];

/** One combined slice each for the bar, so 12 categories stay readable. */
export const OTHER_BAR_COLOR = slate[500];
export const VIEW_BAR_COLOR = slate[300];

/**
 * One served row flattened to the metric vocabulary every view shares.
 *
 * `edits` is computed from the five edit categories rather than read from
 * `write_events` — see SERIES. `views` is the backend's `view_events` (loan
 * opens + doc views + downloads), which is already correct.
 */
export const metricsOf = (r) => {
  // The backend owns `conditions` as of 2026-09-01; older responses fold it
  // into data_updates and need the event_types recovery.
  const backendHasConditions = hasCat(r, "conditions");
  const conditions = backendHasConditions
    ? cat(r, "conditions")
    : CONDITION_TOPICS.reduce((n, t) => n + num(r?.event_types?.[t]), 0);
  const dataEdits = backendHasConditions
    ? cat(r, "data_updates")
    : Math.max(0, cat(r, "data_updates") - conditions);

  const comments = cat(r, "comments");
  const exceptions = cat(r, "exceptions");
  const creations = cat(r, "loan_creations");
  const tasks = cat(r, "task_updates");
  const salesforce = cat(r, "salesforce");
  const automation = cat(r, "automation");
  const unclassified = cat(r, "unclassified");

  return {
    edits: dataEdits + conditions + comments + exceptions + creations,
    dataEdits,
    comments,
    conditions,
    exceptions,
    creations,
    tasks,
    salesforce,
    automation,
    unclassified,
    other: tasks + salesforce + automation + unclassified,
    loanViews: cat(r, "loan_views"),
    docViews: cat(r, "document_views"),
    docDownloads: cat(r, "document_downloads"),
    views: num(r.view_events),
    loans: num(r.loan_count),
    total: num(r.total_events),
  };
};

// Everything summable. `loans` is NOT in here: loan_count is distinct-per-row,
// so adding it up double-counts a loan touched twice — the rolled-up figure
// comes from the union of `loans_touched` instead.
export const SUM_KEYS = [
  "edits",
  "dataEdits",
  "comments",
  "conditions",
  "exceptions",
  "creations",
  "tasks",
  "salesforce",
  "automation",
  "unclassified",
  "other",
  "loanViews",
  "docViews",
  "docDownloads",
  "views",
  "total",
];

const addInto = (acc, r) => {
  SUM_KEYS.forEach((k) => {
    acc[k] += r[k];
  });
  (r.loans_touched || []).forEach((l) => acc.loanSet.add(l));
};

const zeroed = (base) => {
  const a = { ...base, loanSet: new Set() };
  SUM_KEYS.forEach((k) => {
    a[k] = 0;
  });
  return a;
};

// `loans_touched` should always be there; fall back to the widest single child
// rather than reporting zero if it ever isn't.
const distinctLoans = (acc, children) =>
  acc.loanSet.size || Math.max(0, ...children.map((c) => c.loans), 0);

/**
 * Fold the user×day rows into one cumulative row per PERSON. People first,
 * automation grouped after them — a service account with 3,000 events would
 * otherwise sit at the top of a list about who is working.
 */
export const rollupByUser = (dayRows) => {
  const byUser = new Map();

  dayRows.forEach((r) => {
    const key = r.user || "System";
    let a = byUser.get(key);
    if (!a) {
      a = zeroed({
        user: key,
        userName: r.userName,
        sys: isSystem(r),
        activeDays: 0,
        days: [],
      });
      byUser.set(key, a);
    }
    a.activeDays += 1;
    addInto(a, r);
    a.days.push(r);
  });

  const list = [...byUser.values()]
    .map((a) => ({
      ...a,
      loans: distinctLoans(a, a.days),
      days: [...a.days].sort((x, y) =>
        String(y.date).localeCompare(String(x.date)),
      ),
    }))
    .sort((a, b) => b.edits - a.edits || b.total - a.total);

  return [...list.filter((u) => !u.sys), ...list.filter((u) => u.sys)];
};

/** Fold the user×day rows into one row per CALENDAR DAY. */
export const rollupByDay = (dayRows) => {
  const byDate = new Map();

  dayRows.forEach((r) => {
    const key = r.date || "—";
    let a = byDate.get(key);
    if (!a) {
      a = zeroed({ date: key, userSet: new Set(), systemCount: 0, entries: [] });
      byDate.set(key, a);
    }
    if (isSystem(r)) a.systemCount += 1;
    else a.userSet.add(r.user);
    addInto(a, r);
    a.entries.push(r);
  });

  return [...byDate.values()]
    .map((a) => ({
      ...a,
      activeUsers: a.userSet.size,
      loans: distinctLoans(a, a.entries),
      // Busiest person first inside the day.
      entries: [...a.entries].sort(
        (x, y) => y.edits - x.edits || y.total - x.total,
      ),
    }))
    .sort((a, b) => String(b.date).localeCompare(String(a.date)));
};

/** Window-level KPIs for the stat strip. People and automation kept apart. */
export const summarize = (dayRows) => {
  const people = dayRows.filter((r) => !isSystem(r));
  const sys = dayRows.filter((r) => isSystem(r));
  const sum = (arr, k) => arr.reduce((n, r) => n + num(r[k]), 0);
  return {
    activeUsers: new Set(people.map((r) => r.user)).size,
    edits: sum(people, "edits"),
    views: sum(people, "views"),
    // Deliberately the SUM of the daily distincts, not a window-wide union:
    // "how many loans got worked on today, summed over the window" is the
    // workload question. The per-row Loans column is the union.
    loanTouches: sum(people, "loans"),
    // ALL of automation's events, not its "edits": agent and sync writes land
    // in the `automation` category, which by definition isn't an edit — summing
    // edits here would report a busy service account as nearly idle.
    systemEvents: sum(sys, "total"),
  };
};

// ── dates: TWO different days, and they are not the same day ────────────────
/**
 * The backend's `date` is a **UTC calendar day**. Every audit timestamp is
 * written as `dt.datetime.now().isoformat()` on a UTC-clocked server (naive, no
 * offset), and `aggregate_user_activity` buckets on `ts[:10]` — the UTC date.
 *
 * So a UTC day D is NOT the desk's day D. In EDT it runs
 * **D-1 8:00 PM → D 8:00 PM Eastern**, which has two consequences the screen
 * has to handle rather than paper over:
 *
 *   1. Evening work rolls forward. Anything a person does after 8 PM Eastern
 *      lands in the NEXT UTC day's row — which is why, at 10:30 PM on Aug 27,
 *      a bucket dated "Aug 28" exists and looks like tomorrow.
 *   2. "Today" is ambiguous. Comparing a UTC bucket against the BROWSER's local
 *      date is simply a category error, and produced exactly that: the newest
 *      bucket unlabelled and the previous one called "Today".
 *
 * Hence two families below. The grids label the bucket they actually show, in
 * UTC (`relativeDayUtc`). The Timeline regroups to real Eastern days
 * (`easternDateOf`), because an intra-day clock is meaningless otherwise — it
 * was drawing last night's 9 PM session on today's strip.
 */
const pad2 = (n) => String(n).padStart(2, "0");
const isoOf = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** The UTC calendar date `offset` days back — the backend's own reckoning. */
const utcDayIso = (offset = 0) => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - offset);
  return d.toISOString().slice(0, 10);
};

/** The Eastern calendar date of a timestamp — the desk's reckoning. */
export const easternDateOf = (isoString) => {
  const d = convertToEasternTime(normalizeIsoStringToUtc(String(isoString)));
  if (!d || Number.isNaN(d.getTime())) return null;
  return isoOf(d);
};

export const easternTodayIso = () => easternDateOf(new Date().toISOString());

/** The calendar day after a `YYYY-MM-DD`, without tripping over a month end. */
export const nextDayIso = (iso) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + 1);
  return dt.toISOString().slice(0, 10);
};

/** Today/Yesterday for a UTC bucket — what the GRIDS show. */
export const relativeDayUtc = (iso) => {
  if (iso === utcDayIso(0)) return "Today";
  if (iso === utcDayIso(1)) return "Yesterday";
  return null;
};

/** The calendar day before a `YYYY-MM-DD`. */
export const prevDayIso = (iso) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() - 1);
  return dt.toISOString().slice(0, 10);
};

/** Today/Yesterday for a real Eastern day — what the TIMELINE shows. */
export const relativeDayEastern = (iso) => {
  const today = easternTodayIso();
  if (iso === today) return "Today";
  if (iso === prevDayIso(today)) return "Yesterday";
  return null;
};

export const longDay = (iso) => formatDate(iso, "—", "EEE, MMM d");
export const shortDay = (iso) => formatDate(iso, "—", "MMM d");
export const fullDate = (iso) => formatDate(iso, "—", "MMM d, yyyy");


// ── session timeline ────────────────────────────────────────────────────────
/**
 * Turning raw audit events into the day's observed-activity blocks.
 *
 * The events come from `GET /auth/admin/user-activity/timeline` (see
 * useUserActivitySessions.js) — the complete list for one actor on one
 * Eastern day, each already carrying `timestamp`, `event_type`, `sub_event`,
 * the server's `category`, and the loan's identity (`dealId`, nullable
 * `loanNumber` / `loanName` / `sync_loan_id`, `pipeline`, `link`).
 *
 * A block is a run of point events with no gap longer than IDLE_GAP_MIN. Its
 * span is the OBSERVED span between its first and last logged action — not
 * time worked: a loan creation is logged at the instant it was submitted, not
 * across the time spent assembling it, and a gap is "no logged activity", not
 * idle.
 */

/** A break longer than this ends a block and starts the next one. */
export const IDLE_GAP_MIN = 15;

/**
 * The smallest width a block draws at on the strip, in hours. A single event
 * has zero observed span and would otherwise be invisible; this is a VISUAL
 * floor only and never enters a duration figure.
 */
export const MIN_MARKER_H = 1 / 60;

// Sub-events that distinguish opening a file from taking a copy of one.
const DOC_VIEW_SUB_EVENTS = ["view_file", "viewed"];
const DOC_DOWNLOAD_SUB_EVENTS = [
  "download_loan_docs",
  "download_document_package",
  "downloaded",
  "zip_downloaded",
];

/**
 * The condition topics the BACKEND names — deliberately not the legacy
 * `CONDITION_TOPICS` above, which carries an extra `acquisition_conditions_and_stips`
 * for recovering conditions out of old `event_types` payloads. The backend's
 * classifier doesn't list that topic, so it lands in `unclassified` there; if
 * the mirror called it `conditions` the timeline and the grid would disagree
 * about the same event, which is the one thing a mirror must never do.
 */
const CONDITION_TOPICS_MIRROR = [
  "underwriting_conditions",
  "acquisition_underwriting_conditions",
];

const DOC_ACCESS_TOPICS = ["document_access"];
const DOC_TOPICS = ["loan_documents", "acquisition_loan_documents"];
const TASK_TOPICS = ["task_updates"];
const SALESFORCE_TOPICS = [
  "salesforce_chatter",
  "salesforce_field_history",
  "salesforce_child_field_history",
];
const AUTOMATION_TOPICS = [
  "agentic_workflows",
  "loan_opening_review_complete",
  "ai_analysis",
  "ocrolus_parse",
  "ocrolus_signals",
  "ocrolus_summary",
  "parse_documents",
  "box_sync",
  "box_extract",
];
// The real data-entry surface. Explicit, like the backend's — an unknown topic
// must land in `unclassified`, never silently in the edits number.
const DATA_TOPICS = [
  "loan_updates",
  "acquisition_loan_updates",
  "deal_terms",
  "acquisition_deal_terms",
  "borrowers",
  "acquisition_borrowers",
  "entities",
  "acquisition_entities",
  "subject_properties",
  "acquisition_subject_properties",
  "loan_stage",
  "acquisition_loan_stage",
  "loan_closing",
  "acquisition_loan_closing",
  "loan_details",
  "order_updates",
  "key_dates",
  "loan_lock",
  "pricing_updates",
  "underwriting_notes",
  "borrower_submissions",
  "agent_field_revert",
];

/**
 * A JS mirror of `app/services/user_activity.py::activity_category` as
 * rewritten 2026-09-01. It has to stay in step with the Python, or a
 * reconstructed session would categorise an event differently from the row it
 * sits under. **Any change there is a change here.**
 *
 * The rewrite's whole point: every topic is matched explicitly and an unknown
 * one returns `unclassified`. The old version ended in a catch-all
 * `data_updates`, which counted file downloads (one event PER FILE in a zip),
 * task work, Salesforce chatter and agent writes as user data entry — a single
 * bulk download read as thousands of "edits".
 */
export const categoryOfEvent = (ev) => {
  const type = ev?.event_type || "";
  const sub = ev?.sub_event || "";
  if (type === "loan_view") return "loan_views";
  // A GET published on a data topic is a READ (deal_terms:get_loan_terms and
  // friends). They carry an empty `changed_fields` because nothing changed, and
  // counting them as edits showed up as "writes with no payload". Ordered
  // before the topic branching, exactly as the backend does it.
  if (sub.startsWith("get_")) return "loan_views";
  if (DOC_ACCESS_TOPICS.includes(type) || DOC_TOPICS.includes(type)) {
    if (DOC_DOWNLOAD_SUB_EVENTS.some((p) => sub.startsWith(p)))
      return "document_downloads";
    if (DOC_VIEW_SUB_EVENTS.some((p) => sub.startsWith(p)))
      return "document_views";
    // An unknown document_access sub-event is a read, not an edit.
    if (DOC_ACCESS_TOPICS.includes(type)) return "document_views";
    return "data_updates"; // upload / metadata edit = real document work
  }
  if (CONDITION_TOPICS_MIRROR.includes(type)) return "conditions";
  if (type === "comments" || type === "acquisition_comments") return "comments";
  if (type === "exception_updates") return "exceptions";
  if (["loan_creation", "create_loan", "acquisition_loan_creation"].includes(type))
    return "loan_creations";
  if (TASK_TOPICS.includes(type)) return "task_updates";
  if (SALESFORCE_TOPICS.includes(type)) return "salesforce";
  if (AUTOMATION_TOPICS.includes(type)) return "automation";
  if (DATA_TOPICS.includes(type)) return "data_updates";
  return "unclassified";
};

/** The five categories that count as a person editing the loan file. */
const EDIT_CATEGORIES = new Set([
  "data_updates",
  "conditions",
  "comments",
  "exceptions",
  "loan_creations",
]);

export const isEditCategory = (c) => EDIT_CATEGORIES.has(c);

export const isViewCategory = (c) =>
  c === "loan_views" || c === "document_views" || c === "document_downloads";

/**
 * The timeline endpoint's events, in the shape the blocks are built from.
 *
 * No day filter here: the backend already returned exactly the half-open
 * Eastern day (DST included), so slicing `timestamp[:10]` — the UTC date —
 * would throw away everyone's evening. Each timestamp is an instant with its
 * offset; only its CLOCK position is computed, in Eastern.
 *
 * The server's `category` is authoritative (it is `activity_category`, the
 * same function the rollup grid was built from). The JS mirror is only the
 * fallback for a record that somehow arrives without one.
 *
 * Nothing is dropped for missing enrichment: a loan with no number, no name,
 * no current record (`link: null`) keeps its events — `dealId` is the identity.
 */
export const timelineEvents = (events) =>
  (Array.isArray(events) ? events : [])
    .map((ev) => ({
      ts: ev?.timestamp,
      hour: hourOfDay(ev?.timestamp),
      category: ev?.category || categoryOfEvent(ev),
      dealId: ev?.dealId ?? null,
      loanNumber: ev?.loanNumber ?? null,
      loanName: ev?.loanName ?? null,
      syncLoanId: ev?.sync_loan_id ?? null,
      pipeline: ev?.pipeline ?? null,
      // `/loan/{id}` or `/acquisition/{id}` for a loan that still exists in
      // either pipeline; null means "show the identity, offer no navigation".
      link: ev?.link ?? null,
      // Kept so a block can be opened up and show WHAT changed, not just how
      // much. `changed_fields` is the diff (or, on live-sink records, the
      // submitted body) — see `fieldChange` for the two shapes.
      eventType: ev?.event_type || "",
      subEvent: ev?.sub_event || "",
      changedFields: ev?.changed_fields || null,
      statusChange: ev?.status_change ?? null,
    }))
    .filter((e) => e.hour !== null)
    .sort((a, b) => a.hour - b.hour);

/**
 * Where on the clock an event sits, as a fractional hour in EASTERN time —
 * the zone the desk works in, and the one `formatDateTime` pins the rest of the
 * app to. A bare `new Date(ts).getHours()` would read the VIEWER's zone, which
 * would move everyone's workday depending on who opened the screen.
 */
export const hourOfDay = (isoString) => {
  const eastern = convertToEasternTime(normalizeIsoStringToUtc(String(isoString)));
  if (!eastern || Number.isNaN(eastern.getTime())) return null;
  return eastern.getHours() + eastern.getMinutes() / 60 + eastern.getSeconds() / 3600;
};

/**
 * Split a day's events into blocks: walk them in order and start a new block
 * wherever more than IDLE_GAP_MIN passed with nothing logged. This is the
 * whole point of the view — the gaps are the finding, not the blocks.
 *
 * Each block carries two ends: `end` is the OBSERVED last event (so
 * `end - start` is the observed span, zero for a lone event) and `drawEnd` is
 * where the strip stops drawing it (never narrower than MIN_MARKER_H). The
 * visual floor must never leak into a duration figure.
 */
export const buildSessions = (events) => {
  const out = [];
  let cur = null;

  events.forEach((e) => {
    if (!cur || (e.hour - cur.end) * 60 > IDLE_GAP_MIN) {
      cur = {
        start: e.hour,
        end: e.hour,
        edits: 0,
        views: 0,
        other: 0,
        events: 0,
        // The events themselves, so the session can be expanded into "what
        // changed, on which loan".
        items: [],
        // Per-category tally so the session can explain WHAT the edits were,
        // not just how many — same vocabulary as the grid's columns.
        categories: {},
        // Keyed by dealId: the same loan touched twenty times is one loan, and
        // the id is what makes it openable.
        loanMap: new Map(),
      };
      out.push(cur);
    }
    cur.end = e.hour;
    cur.events += 1;
    cur.items.push(e);
    // Same definition as the grid: task work, Salesforce and agent writes are
    // real activity but they are NOT edits, so they land in `other`.
    if (isViewCategory(e.category)) cur.views += 1;
    else if (isEditCategory(e.category)) cur.edits += 1;
    cur.categories[e.category] = (cur.categories[e.category] || 0) + 1;
    if (!isViewCategory(e.category) && !isEditCategory(e.category))
      cur.other += 1;
    // `dealId` is the stable identity even for a loan with no number or name;
    // a number-only key is the fallback for a record that has neither.
    const id = e.dealId || (e.loanNumber != null ? `#${e.loanNumber}` : null);
    if (id && !cur.loanMap.has(id)) {
      cur.loanMap.set(id, {
        dealId: e.dealId || null,
        loanNumber: e.loanNumber ?? null,
        loanName: e.loanName ?? null,
        syncLoanId: e.syncLoanId ?? null,
        pipeline: e.pipeline ?? null,
        link: e.link ?? null,
      });
    }
  });

  return out.map((s) => ({
    start: s.start,
    end: s.end,
    drawEnd: Math.max(s.end, s.start + MIN_MARKER_H),
    edits: s.edits,
    views: s.views,
    other: s.other,
    events: s.events,
    categories: s.categories,
    items: s.items,
    loanList: [...s.loanMap.values()],
    loans: s.loanMap.size,
  }));
};

/**
 * One field's change, out of the two shapes `changed_fields` comes in.
 *
 * The retroactive writer stores a real diff — `{prior, updated}` — while the
 * LIVE listener sink stores the submitted request body, i.e. the NEW value
 * only (`_summarize_changed_fields` in structured_audit.py). So "before" is
 * available for some events and genuinely unknown for others; this returns
 * `hasPrior` so the UI can say "set to X" rather than invent an empty before.
 */
export const fieldChange = (value) => {
  const isPair =
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    ("prior" in value || "updated" in value);
  return isPair
    ? { hasPrior: true, from: value.prior, to: value.updated }
    : { hasPrior: false, from: undefined, to: value };
};

/**
 * A session's edit categories mapped onto SERIES, so the hover card in the
 * timeline speaks the same language (and wears the same colours) as the grid's
 * Breakdown bar. `data_updates` minus conditions, exactly as `metricsOf` does
 * it for a rollup row.
 */
export const sessionEditMix = (session) => {
  const c = session?.categories || {};
  // No subtraction: `categoryOfEvent` reads the topic straight off the raw
  // event, so conditions never entered data_updates in the first place.
  const byKey = {
    dataEdits: num(c.data_updates),
    conditions: num(c.conditions),
    comments: num(c.comments),
    exceptions: num(c.exceptions),
    creations: num(c.loan_creations),
  };
  return SERIES.map((s) => ({ ...s, value: num(byKey[s.key]) }));
};

/** 9.75 → "9:45 AM". Sessions are hours-of-day, so no Date parsing is involved. */
export const formatHour = (h) => {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  const ampm = hh >= 12 ? "PM" : "AM";
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${h12}:${String(mm % 60).padStart(2, "0")} ${ampm}`;
};

/** 1.75 → "1 h 45 m"; 0.2 → "12 min"; 0 → "< 1 min" (a lone event, not a minute of work). */
export const formatDuration = (hours) => {
  const m = Math.round(hours * 60);
  if (m < 1) return "< 1 min";
  return m >= 60
    ? `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")} m`
    : `${m} min`;
};
